import { useCallback } from 'react'
import type { RouterOutputs } from '@/lib/trpc'
import { trpc } from '../../../../lib/trpc.js'
import { useResource } from './use-resource'

export interface RecipeImplementationItem {
  language: string
  code: string
  testCode: string
  testExpectedOutput: string
  lastValidatedAt: string | null
}

export interface LinkedSubtopic {
  id: string
  name: string
  slug: string
}

export interface Recipe {
  id: string
  name: string
  slug: string
  shortDescription: string | null
  descriptionDetails: string | null
  pseudocode: string | null
  order: number
  createdAt: string
  updatedAt: string
  implementations: RecipeImplementationItem[]
  subtopics: LinkedSubtopic[]
  languageSummary: {
    python?: boolean
    javascript?: boolean
  }
}

export interface RecipeFormData {
  name: string
  shortDescription?: string | null
  descriptionDetails?: string | null
  pseudocode?: string | null
  implementations?: Array<{
    language: string
    code: string
    testCode: string
    testExpectedOutput: string
  }>
  subtopicIds?: string[]
}

export interface ValidationResult {
  success: boolean
  actual: string
  expected: string
  error?: string
  stderr?: string
}

const selectRecipes = (
  data: RouterOutputs['admin']['recipes']['list'] | undefined,
): Recipe[] => data?.data ?? []

export function useRecipes() {
  const utils = trpc.useUtils()

  const invalidate = useCallback(() => {
    utils.admin.recipes.list.invalidate()
  }, [utils])

  const {
    items: recipes,
    loading,
    error,
    refresh,
    create,
    update,
    remove,
  } = useResource({
    query: trpc.admin.recipes.list.useQuery(),
    select: selectRecipes,
    invalidate,
    create: trpc.admin.recipes.create,
    update: trpc.admin.recipes.update,
    remove: trpc.admin.recipes.delete,
  })

  const upsertImplementationMutation =
    trpc.admin.recipes.upsertImplementation.useMutation({
      onSuccess: invalidate,
    })

  const validateMutation = trpc.admin.recipes.validate.useMutation({
    onSuccess: (result) => {
      if (result.success) invalidate()
    },
  })

  const createRecipe = async (data: RecipeFormData): Promise<Recipe> => {
    return create({
      name: data.name,
      shortDescription: data.shortDescription,
      descriptionDetails: data.descriptionDetails,
      pseudocode: data.pseudocode,
      implementations: data.implementations?.map((impl) => ({
        language: impl.language as 'python' | 'javascript',
        code: impl.code,
        testCode: impl.testCode,
        testExpectedOutput: impl.testExpectedOutput,
      })),
      subtopicIds: data.subtopicIds,
    }) as Promise<Recipe>
  }

  const updateRecipe = async (
    id: string,
    data: Partial<RecipeFormData>,
  ): Promise<Recipe> => {
    return update({
      id,
      name: data.name,
      shortDescription: data.shortDescription,
      descriptionDetails: data.descriptionDetails,
      pseudocode: data.pseudocode,
      implementations: data.implementations?.map((impl) => ({
        language: impl.language as 'python' | 'javascript',
        code: impl.code,
        testCode: impl.testCode,
        testExpectedOutput: impl.testExpectedOutput,
      })),
      subtopicIds: data.subtopicIds,
    }) as Promise<Recipe>
  }

  const deleteRecipe = async (id: string): Promise<void> => {
    await remove({ id })
  }

  const getRecipe = async (id: string): Promise<Recipe> => {
    return utils.admin.recipes.getById.fetch({ id }) as Promise<Recipe>
  }

  const updateImplementation = async (
    id: string,
    language: string,
    data: {
      code?: string
      testCode?: string
      testExpectedOutput?: string
    },
  ): Promise<Recipe> => {
    return upsertImplementationMutation.mutateAsync({
      id,
      language: language as 'python' | 'javascript',
      ...data,
    }) as Promise<Recipe>
  }

  const validateRecipe = async (
    id: string,
    language: string,
    options?: {
      code?: string
      testCode?: string
      testExpectedOutput?: string
    },
  ): Promise<ValidationResult> => {
    return validateMutation.mutateAsync({
      id,
      language: language as 'python' | 'javascript',
      ...options,
    }) as Promise<ValidationResult>
  }

  return {
    recipes,
    loading,
    error,
    refresh,
    createRecipe,
    updateRecipe,
    deleteRecipe,
    getRecipe,
    updateImplementation,
    validateRecipe,
  }
}
